import React, { useEffect, useState, useRef } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Modal,
  TextInput,
  TableToolbarAction,
  TableToolbarMenu,
  Dropdown,
  InlineLoading,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";
import { debounce } from "lodash";

const headers = [
  { key: "districtName", header: "District Name" },
  { key: "state", header: "State" },
  { key: "country", header: "Country" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "actions", header: "Actions" },
];

const District = () => {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [districtName, setDistrictName] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [countries, setCountries] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const userId = 1;
  const searchDebounceRef = useRef();

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        searchDebounceRef.current.cancel();
      }
    };
  }, []);

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await callApi("GET", "/countries/");
      const formatted = response.data.map((c) => ({ id: c.id, name: c.c_name }));
      setCountries(formatted);
    } catch (error) {
      console.error("Failed to fetch countries:", error.message);
    }
  };

  // Fetch all districts
  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const res = await callApi("GET", "/districts/");
    
      let data = res.data;
      
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (Array.isArray(data.districts)) {
          data = data.districts;
        } else if (Array.isArray(data.data)) {
          data = data.data;
        } else if (Array.isArray(data.results)) {
          data = data.results;
        }
      }
      
      if (!Array.isArray(data)) {
        setRows([]);
        setAllRows([]);
        return;
      }

      if (data.length === 0) {
        setRows([]);
        setAllRows([]);
        return;
      }
      
      const formatted = data.map((item) => ({
        id: String(item.id || item.district_id || item.DISTRICT_ID),
        districtName: item.name || item.district_name || item.DISTRICT_NAME || "N/A",
        state: item.state_name || item.STATE_NAME || "N/A",
        country: item.c_name || item.country_name || item.COUNTRY_NAME || "N/A",
        status: item.status || item.STATUS || "Active",
        createdTime: item.created_at || item.CREATED_AT || item.createdAt || "N/A",
        createdBy:item.created_user|| "N/A",
        stateId: item.state_id || item.STATE_ID,
        countryId: item.country_id || item.COUNTRY_ID,
      }));
      
      setAllRows(formatted);
      setRows(formatted);
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all states
  const fetchAllStates = async () => {
    try {
      const res = await callApi("GET", "/state-masters/");
      return res.data;
    } catch (error) {
      console.error("Failed to fetch states:", error.message);
      return [];
    }
  };

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    
    if (searchDebounceRef.current) {
      searchDebounceRef.current.cancel();
    }
    
    searchDebounceRef.current = debounce((searchValue) => {
      if (!searchValue.trim()) {
        setRows(allRows);
      } else {
        const filtered = allRows.filter((row) =>
          row.districtName.toLowerCase().includes(searchValue.toLowerCase()) ||
          row.state.toLowerCase().includes(searchValue.toLowerCase()) ||
          row.country.toLowerCase().includes(searchValue.toLowerCase())
        );
        setRows(filtered);
      }
    }, 300);
    
    searchDebounceRef.current(value);
  };

  useEffect(() => {
    fetchCountries();
    fetchDistricts();
  }, []);

  const openEditModal = (rowData) => {
    if (!rowData) return;
    
    setIsEditMode(true);
    setErrors({});
    setDistrictName(rowData.districtName);
    setSelectedRow(rowData);

    const countryObj = countries.find((c) => c.id === rowData.countryId);
    setSelectedCountry(countryObj || null);

    if (countryObj) {
      fetchAllStates().then(allStates => {
        const filteredStates = allStates
          .filter((s) => s.country_id === countryObj.id)
          .map((s) => ({ id: s.id, name: s.state_name }));
        
        setStatesOptions(filteredStates);
        
        const stateObj = filteredStates.find((s) => s.id === rowData.stateId);
        setSelectedState(stateObj || null);
        setIsModalOpen(true);
      });
    } else {
      setIsModalOpen(true);
    }
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setDistrictName("");
    setSelectedRow(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setStatesOptions([]);
    setErrors({});
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setDistrictName("");
    setSelectedRow(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setStatesOptions([]);
    setErrors({});
    setIsEditMode(false);
  };

  const validateForm = () => {
    let temp = {};
    if (!districtName.trim()) temp.districtName = "District name is required";
    if (!selectedCountry) temp.country = "Country is required";
    if (!selectedState) temp.state = "State is required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      if (selectedRow) {
        await callApi("PUT", "/districts/", {
          DISTRICT_ID: selectedRow.id,
          DISTRICT_NAME: districtName,
          STATE_ID: selectedState.id,
          UPDATED_BY_USER_ID: userId,
        });
      } else {
        await callApi("POST", "/districts/", {
          DISTRICT_NAME: districtName,
          STATE_ID: selectedState.id,
          CREATED_BY_USER_ID: userId,
        });
      }
      
      await fetchDistricts();
      resetModal();
    } catch (error) {
      console.error("Failed to save district:", error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await callApi("DELETE", `/districts/${deleteId}`);
      await fetchDistricts();
    } catch (error) {
      console.error("Failed to delete district:", error.message);
      alert(`Failed to delete: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <div>
      {loading && (
        <div style={{ padding: '1rem' }}>
          <InlineLoading description="Loading..." />
        </div>
      )}
      
      <TableContainer title="Districts">
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch 
              persistent 
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search districts by name, state, or country"
            />
            <Button renderIcon={Add} onClick={openAddModal} disabled={loading}>
              Add New
            </Button>
          </TableToolbarContent>
        </TableToolbar>

        <DataTable rows={rows} headers={headers}>
          {({ rows, headers, getHeaderProps, getRowProps }) => (
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={headers.length}>
                      No districts found. Click "Add New" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map((cell) => {
                        if (cell.info.header === "actions") {
                          return (
                            <TableCell key={cell.id}>
                              <TableToolbarMenu>
                                <TableToolbarAction 
                                  onClick={() => {
                                    if (row.original) {
                                      openEditModal(row.original);
                                    } else {
                                      const foundRow = allRows.find(r => r.id === row.id);
                                      if (foundRow) {
                                        openEditModal(foundRow);
                                      }
                                    }
                                  }}
                                >
                                  Edit
                                </TableToolbarAction>
                                <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                                  Delete
                                </TableToolbarAction>
                              </TableToolbarMenu>
                            </TableCell>
                          );
                        }
                        return <TableCell key={cell.id}>{cell.value}</TableCell>;
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </DataTable>
      </TableContainer>

      <Modal
        open={isModalOpen}
        onRequestClose={resetModal}
        modalHeading={isEditMode ? "Edit District" : "Add New District"}
        primaryButtonText={isEditMode ? "Update" : "Save"}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
      >
        <div style={{ marginBottom: '1rem' }}>
          <Dropdown
            id="country"
            titleText="Country"
            label="Select Country"
            items={countries}
            itemToString={(item) => item?.name || ""}
            selectedItem={selectedCountry}
            onChange={({ selectedItem }) => {
              setSelectedCountry(selectedItem);
              setSelectedState(null);
              if (selectedItem) {
                fetchAllStates().then(allStates => {
                  const filtered = allStates
                    .filter((s) => s.country_id === selectedItem.id)
                    .map((s) => ({ id: s.id, name: s.state_name }));
                  setStatesOptions(filtered);
                });
              }
            }}
            invalid={!!errors.country}
            invalidText={errors.country}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <Dropdown
            id="state"
            titleText="State"
            label="Select State"
            items={statesOptions}
            itemToString={(item) => item?.name || ""}
            selectedItem={selectedState}
            onChange={({ selectedItem }) => setSelectedState(selectedItem)}
            invalid={!!errors.state}
            invalidText={errors.state}
            disabled={!selectedCountry}
          />
        </div>

        <TextInput
          id="district-name"
          labelText="District Name"
          value={districtName}
          onChange={(e) => setDistrictName(e.target.value)}
          invalid={!!errors.districtName}
          invalidText={errors.districtName}
        />
      </Modal>

      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this district?</p>
      </Modal>
    </div>
  );
};

export default District;